/**
 * 이벤트 등록/수정 페이지
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Form, Button, Spinner } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import RichTextEditor from '../../components/common/RichTextEditor';
import { eventService } from '../../services/eventService';
import { Event } from '../../types/event';
import apiClient from '../../services/api';

const EventForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const isNew = location.pathname.includes('/new');
  const eventId = isNew ? 0 : (id ? parseInt(id, 10) : 0);

  const [formData, setFormData] = useState<Partial<Event>>({
    event_name: '',
    event_code: '',
    event_type: '',
    description: '',
    image_url: '',
    start_date: '',
    end_date: '',
    status: 'active',
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => eventService.getEvent(eventId),
    enabled: !!eventId && eventId > 0 && !isNew,
  });

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Event>) => {
      // 이미지 업로드
      if (imageFile) {
        const formData = new FormData();
        formData.append('image', imageFile);
        const response = await apiClient.post<{ data: { url: string } }>(
          '/api/upload/image',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
        data.image_url = response.data.data.url;
      }

      return isNew ? eventService.createEvent(data) : eventService.updateEvent(eventId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      alert(isNew ? '이벤트가 등록되었습니다.' : '이벤트가 수정되었습니다.');
      navigate('/admin/events');
    },
    onError: (error: any) => {
      alert(`저장 실패: ${error.message}`);
    },
  });

  useEffect(() => {
    if (event) {
      setFormData({
        event_name: event.event_name,
        event_code: event.event_code,
        event_type: event.event_type,
        description: event.description,
        image_url: event.image_url,
        start_date: event.start_date,
        end_date: event.end_date,
        status: event.status,
      });
      if (event.image_url) {
        setImagePreview(event.image_url);
      }
    }
  }, [event]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('이미지 크기는 10MB 이하여야 합니다.');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('이미지 파일만 업로드 가능합니다.');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.event_name?.trim()) {
      alert('이벤트명을 입력해주세요.');
      return;
    }

    if (!formData.event_code?.trim()) {
      alert('이벤트 코드를 입력해주세요.');
      return;
    }

    if (!formData.start_date || !formData.end_date) {
      alert('시작일과 종료일을 입력해주세요.');
      return;
    }

    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      alert('종료일은 시작일보다 이후여야 합니다.');
      return;
    }

    saveMutation.mutate(formData);
  };

  if (isLoading && !isNew) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>{isNew ? '이벤트 등록' : '이벤트 수정'}</h1>
          <Button variant="secondary" onClick={() => navigate('/admin/events')}>
            목록으로
          </Button>
        </div>

        <Form onSubmit={handleSubmit}>
          <Card className="mb-4">
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>이벤트명 *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.event_name || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      event_name: e.target.value,
                    }))
                  }
                  placeholder="이벤트명을 입력하세요..."
                  required
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>이벤트 코드 *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.event_code || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      event_code: e.target.value,
                    }))
                  }
                  placeholder="예: EVENT_2024_01"
                  required
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                />
                <Form.Text style={{ color: '#888' }}>고유한 이벤트 코드를 입력하세요.</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>이벤트 유형</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.event_type || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      event_type: e.target.value,
                    }))
                  }
                  placeholder="예: 할인, 프로모션, 이벤트 등"
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>대표 이미지</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                />
                {imagePreview && (
                  <div className="mt-2">
                    <img
                      src={imagePreview}
                      alt="이벤트 이미지 미리보기"
                      style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '4px' }}
                    />
                  </div>
                )}
                <Form.Text style={{ color: '#888' }}>10MB 이하의 이미지 파일만 업로드 가능합니다.</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>시작일 *</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.start_date ? formData.start_date.substring(0, 16) : ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      start_date: e.target.value ? `${e.target.value}:00` : '',
                    }))
                  }
                  required
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>종료일 *</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.end_date ? formData.end_date.substring(0, 16) : ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      end_date: e.target.value ? `${e.target.value}:00` : '',
                    }))
                  }
                  required
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>상태</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.value as 'active' | 'inactive' | 'ended',
                    }))
                  }
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                >
                  <option value="active">진행중</option>
                  <option value="inactive">비활성</option>
                  <option value="ended">종료</option>
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>설명</Form.Label>
                <RichTextEditor
                  value={formData.description || ''}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: value,
                    }))
                  }
                  placeholder="이벤트 설명을 입력하세요..."
                  height="400px"
                />
              </Form.Group>
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={() => navigate('/admin/events')}>
              취소
            </Button>
            <Button variant="primary" type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  저장 중...
                </>
              ) : (
                '저장'
              )}
            </Button>
          </div>
        </Form>
      </div>
    </AdminLayout>
  );
};

export default EventForm;

